import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  ToggleLeft, ToggleRight, Sparkles, ShoppingCart, AlertTriangle,
  Calendar, Clock, ShieldCheck, ExternalLink,
} from 'lucide-react';
import {
  getEnrollmentModeAdmin, setEnrollmentModeAdmin, type EnrollmentMode,
} from '../services/admin_api';
import { extractErrorMessage } from '@/services/api';

// Enrollment page: single global toggle that switches the landing page
// between paid checkout (default) and free workshop signup. Both modes
// route through the same backend account-creation path; the difference
// is which UI the user sees and how credits/expiry are granted.
//
// Auto-expire: when an admin flips to "free" they pick a duration
// (minutes / hours / days / unbounded). The backend auto-reverts to paid
// past that window so a forgotten toggle doesn't leak free signups forever.
// Minute-grain is intentional — workshops often run a tight 10-30 min
// signup window and admins want the toggle to slam shut on time.
//
// minutes=0 → "Until I disable" (no expiry).
const PRESETS = [
  { label: '10 minutes', minutes: 10 },
  { label: '30 minutes', minutes: 30 },
  { label: '1 hour',     minutes: 60 },
  { label: '4 hours',    minutes: 60 * 4 },
  { label: '1 day',      minutes: 60 * 24 },
  { label: '3 days',     minutes: 60 * 24 * 3 },
  { label: '7 days',     minutes: 60 * 24 * 7 },
  { label: 'Until I disable', minutes: 0 },
];

export function Enrollment() {
  const [state, setState] = useState<EnrollmentMode | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Default to "1 hour" — sane for a typical workshop signup window.
  // Admin can override per session.
  const [presetIdx, setPresetIdx] = useState(2);
  const [confirmFree, setConfirmFree] = useState(false);
  // Free-mode per-signup credit grant. Default 10,000 (matches backend
  // FreeWorkshopCredits). Seeded from the server's stored value on first
  // load only — the 30s poll must not clobber an admin mid-edit.
  const DEFAULT_FREE_CREDITS = 10000;
  const [credits, setCredits] = useState<number>(DEFAULT_FREE_CREDITS);
  const creditsSeeded = useRef(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getEnrollmentModeAdmin();
      setState(data);
      if (!creditsSeeded.current && typeof data.free_mode_credits === 'number') {
        setCredits(data.free_mode_credits);
        creditsSeeded.current = true;
      }
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Could not load enrollment mode'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Light polling so two admins on different tabs see the same state.
    const id = window.setInterval(load, 30_000);
    return () => window.clearInterval(id);
  }, []);

  // Client-side bounds mirror the backend (1..100,000). The server
  // re-validates, so this is just fast feedback.
  const CREDITS_MIN = 1;
  const CREDITS_MAX = 100000;
  const creditsValid = Number.isInteger(credits) && credits >= CREDITS_MIN && credits <= CREDITS_MAX;

  const switchToFree = async () => {
    if (!creditsValid) {
      toast.error(`Credits must be a whole number between ${CREDITS_MIN} and ${CREDITS_MAX.toLocaleString()}`);
      return;
    }
    setSaving(true);
    try {
      const preset = PRESETS[presetIdx];
      const expiresAt = preset.minutes > 0
        ? new Date(Date.now() + preset.minutes * 60 * 1000).toISOString()
        : null;
      await setEnrollmentModeAdmin('free', expiresAt, credits);
      toast.success(`Free mode active — ${credits.toLocaleString()} credits${preset.minutes > 0 ? ` for ${preset.label.toLowerCase()}` : ''}`);
      setConfirmFree(false);
      await load();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Could not switch to free mode'));
    } finally {
      setSaving(false);
    }
  };

  const switchToPaid = async () => {
    setSaving(true);
    try {
      await setEnrollmentModeAdmin('paid', null);
      toast.success('Paid mode active');
      await load();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Could not switch to paid mode'));
    } finally {
      setSaving(false);
    }
  };

  const isFree = state?.mode === 'free';
  // Credits to show in the status/info copy — the server's stored value,
  // or the default when never configured.
  const displayCredits = state?.free_mode_credits ?? DEFAULT_FREE_CREDITS;
  const expiresAt = state?.free_mode_expires ? new Date(state.free_mode_expires) : null;
  const expiresValid = expiresAt && !isNaN(expiresAt.getTime());

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <ShoppingCart className="h-6 w-6 text-primary" /> Enrollment Mode
        </h1>
        <p className="text-sm text-text-secondary">
          Switch landing-page signup between paid credit packs and free workshop enrollment.
        </p>
      </div>

      {loading ? (
        <div className="rounded-lg border border-border bg-white p-8 text-center text-text-secondary">
          Loading…
        </div>
      ) : (
        <>
          {/* Status card — big visual indicator */}
          <div className={`relative overflow-hidden rounded-xl border-2 p-6 sm:p-8 transition-all ${
            isFree
              ? 'border-emerald-500/40 bg-gradient-to-br from-emerald-50 to-cyan-50'
              : 'border-blue-500/40 bg-gradient-to-br from-blue-50 to-indigo-50'
          }`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className={`rounded-xl p-3 ${
                  isFree ? 'bg-emerald-500 text-white' : 'bg-blue-500 text-white'
                }`}>
                  {isFree ? <Sparkles className="h-6 w-6" /> : <ShoppingCart className="h-6 w-6" />}
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-text-secondary font-semibold">
                    Currently
                  </div>
                  <div className="text-2xl font-bold">
                    {isFree ? 'Free Workshop Mode' : 'Paid Checkout Mode'}
                  </div>
                  <div className="text-sm text-text-secondary mt-1">
                    {isFree
                      ? `Visitors get ${displayCredits.toLocaleString()} credits + 1 year validity, no payment required.`
                      : 'Visitors see Razorpay credit packs (₹799 / ₹1099 / ₹2099, incl. 18% GST).'}
                  </div>
                </div>
              </div>
              {isFree
                ? <ToggleRight className="h-10 w-10 text-emerald-500 shrink-0" />
                : <ToggleLeft  className="h-10 w-10 text-blue-500 shrink-0" />}
            </div>

            {isFree && expiresValid && (
              <div className="mt-5 flex items-center gap-2 rounded-lg bg-white/70 backdrop-blur px-4 py-3 text-sm">
                <Clock className="h-4 w-4 text-amber-600 shrink-0" />
                <span className="text-text-primary">
                  Auto-disables at <strong>{expiresAt!.toLocaleString()}</strong>
                  <span className="ml-2 text-text-secondary">
                    ({formatRelative(expiresAt!)})
                  </span>
                </span>
              </div>
            )}
            {isFree && !expiresValid && (
              <div className="mt-5 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <span>
                  <strong>No auto-disable set.</strong> Free mode stays on until you turn it off manually.
                  Use the controls below to add an expiry, or click "Switch to Paid" when the workshop ends.
                </span>
              </div>
            )}
          </div>

          {/* Action panel */}
          {isFree ? (
            <div className="rounded-lg border border-border bg-white p-6">
              <h2 className="text-lg font-semibold mb-2">Switch back to paid checkout</h2>
              <p className="text-sm text-text-secondary mb-4">
                New visitors to the landing page will see the paid credit packs and pay via Razorpay.
                Existing free accounts stay active until their 1-year validity expires.
              </p>
              <button
                type="button"
                className="btn-primary"
                disabled={saving}
                onClick={switchToPaid}
              >
                {saving ? 'Switching…' : 'Switch to Paid Mode'}
              </button>
            </div>
          ) : (
            <div className="rounded-lg border border-border bg-white p-6">
              <h2 className="text-lg font-semibold mb-2">Switch to free workshop mode</h2>
              <p className="text-sm text-text-secondary mb-5">
                Anyone with the landing-page link can self-enroll for{' '}
                {(creditsValid ? credits : displayCredits).toLocaleString()} credits valid 1 year.
                Pick how long this stays on — the system auto-reverts to paid after that window.
              </p>

              <div className="mb-5">
                <label className="block text-xs font-semibold uppercase tracking-wide text-text-secondary mb-2">
                  Auto-disable after
                </label>
                <div className="flex flex-wrap gap-2">
                  {PRESETS.map((p, i) => (
                    <button
                      key={p.label}
                      type="button"
                      onClick={() => setPresetIdx(i)}
                      className={`px-3 py-1.5 rounded-md text-sm border transition ${
                        presetIdx === i
                          ? 'border-primary bg-primary/10 text-primary font-medium'
                          : 'border-border bg-white hover:bg-black/5 text-text-primary'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-5">
                <label
                  htmlFor="free-credits"
                  className="block text-xs font-semibold uppercase tracking-wide text-text-secondary mb-2"
                >
                  Credits per signup
                </label>
                <input
                  id="free-credits"
                  type="number"
                  min={CREDITS_MIN}
                  max={CREDITS_MAX}
                  step={500}
                  value={Number.isNaN(credits) ? '' : credits}
                  onChange={(e) => setCredits(parseInt(e.target.value, 10))}
                  className={`w-44 rounded-md border px-3 py-2 text-sm text-text-primary outline-none transition focus:ring-2 focus:ring-primary/30 ${
                    creditsValid ? 'border-border bg-white' : 'border-red-400 bg-red-50'
                  }`}
                />
                <p className={`mt-1.5 text-xs ${creditsValid ? 'text-text-secondary' : 'text-red-600'}`}>
                  {creditsValid
                    ? `Each new free signup gets ${credits.toLocaleString()} credits, valid 1 year.`
                    : `Enter a whole number between ${CREDITS_MIN} and ${CREDITS_MAX.toLocaleString()}.`}
                </p>
              </div>

              {!confirmFree ? (
                <button
                  type="button"
                  className="btn-primary"
                  disabled={saving || !creditsValid}
                  onClick={() => setConfirmFree(true)}
                >
                  Enable Free Mode
                </button>
              ) : (
                <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4">
                  <div className="flex items-start gap-2 mb-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                    <div className="text-sm">
                      <strong>Are you sure?</strong> Once active, anyone who visits the landing page can create an account.
                      Free mode is intended for workshop attendees only — share the link only with your audience.
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="btn-primary bg-amber-600 hover:bg-amber-700"
                      disabled={saving}
                      onClick={switchToFree}
                    >
                      {saving ? 'Enabling…' : `Yes, Enable (${PRESETS[presetIdx].label})`}
                    </button>
                    <button
                      type="button"
                      className="btn-ghost"
                      disabled={saving}
                      onClick={() => setConfirmFree(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Info card — what each mode does */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-lg border border-border bg-white p-5">
              <div className="flex items-center gap-2 mb-3">
                <ShoppingCart className="h-4 w-4 text-blue-500" />
                <h3 className="font-semibold text-sm">Paid Mode (Default)</h3>
              </div>
              <ul className="text-xs text-text-secondary space-y-1.5">
                <li>• Landing page shows ₹799 / ₹1099 / ₹2099 credit packs (incl. 18% GST)</li>
                <li>• Visitor pays via Razorpay (UPI / Card / Netbanking)</li>
                <li>• Account auto-created on payment captured</li>
                <li>• Credits valid 1 year from purchase, extended on renewal</li>
                <li>• Welcome email sent via queue</li>
              </ul>
            </div>
            <div className="rounded-lg border border-border bg-white p-5">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="h-4 w-4 text-emerald-500" />
                <h3 className="font-semibold text-sm">Free Mode (Workshops)</h3>
              </div>
              <ul className="text-xs text-text-secondary space-y-1.5">
                <li>• Landing page shows email + phone signup form</li>
                <li>• No payment — account created instantly</li>
                <li>• {displayCredits.toLocaleString()} credits, valid for 1 year (admin-configurable)</li>
                <li>• Duplicate email/phone → "already enrolled, upgrade" prompt</li>
                <li>• Auto-disables after the duration you set</li>
              </ul>
            </div>
          </div>

          {/* Quick-link to landing page */}
          <div className="rounded-lg border border-border bg-white p-5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 text-emerald-500" />
              <div>
                <div className="font-semibold text-sm">Landing page</div>
                <div className="text-xs text-text-secondary">Test the live signup experience</div>
              </div>
            </div>
            <a
              href="https://autopilot.iraskills.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost text-sm"
            >
              Open
              <ExternalLink className="ml-1 h-3.5 w-3.5" />
            </a>
          </div>
        </>
      )}
    </div>
  );
}

// formatRelative renders "in 3h 24m" / "in 2 days" without a date library.
// Returns "now" when the gap is sub-minute.
function formatRelative(target: Date): string {
  const ms = target.getTime() - Date.now();
  if (ms < 0) return 'overdue';
  const totalMin = Math.floor(ms / 60_000);
  if (totalMin < 1) return 'now';
  if (totalMin < 60) return `in ${totalMin} min`;
  const hours = Math.floor(totalMin / 60);
  const min = totalMin % 60;
  if (hours < 24) return `in ${hours}h ${min}m`;
  const days = Math.floor(hours / 24);
  return `in ${days} day${days > 1 ? 's' : ''}`;
}

// Suppress unused-import warnings when Calendar isn't used in a render branch.
// (Kept imported for forward-compat — likely re-used when we add the
// schedule-future-window feature.)
const _Calendar = Calendar; void _Calendar;
